import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Telegram as TelegramIcon,
  SmartToy as BotIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Code as CodeIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import api from "../../api/api";
import { useErrorHandler } from "../../../hooks/useErrorHandler";

// Иконки и лейблы для типов каналов
const CHANNEL_TYPES = {
  telegram: { label: "Telegram", icon: <TelegramIcon />, color: "#0088cc" },
  whatsapp: { label: "WhatsApp", icon: <BotIcon />, color: "#25D366" },
  avito: { label: "Avito", icon: <BotIcon />, color: "#00AAFF" },
  viber: { label: "Viber", icon: <BotIcon />, color: "#7360F2" },
  vk: { label: "ВКонтакте", icon: <BotIcon />, color: "#0077FF" },
};

function ChannelSettings() {
  const companyId = localStorage.getItem("company_id");
  const { handleError, showSuccess } = useErrorHandler();

  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [showToken, setShowToken] = useState(false);

  // Личный Telegram
  const [personalChannels, setPersonalChannels] = useState([]);

  // Виджет для сайта
  const [widgetGreeting, setWidgetGreeting] = useState("Здравствуйте! Чем могу помочь?");
  const [widgetColor, setWidgetColor] = useState("#1976d2");
  const [widgetSaving, setWidgetSaving] = useState(false);
  const [personalDialogOpen, setPersonalDialogOpen] = useState(false);
  const [personalStep, setPersonalStep] = useState('phone'); // 'phone' | 'code'
  const [personalPhone, setPersonalPhone] = useState('');
  const [personalCode, setPersonalCode] = useState('');
  const [personalCodeHash, setPersonalCodeHash] = useState('');
  const [personalPassword, setPersonalPassword] = useState('');
  const [personalNeed2FA, setPersonalNeed2FA] = useState(false);
  const [personalLoading, setPersonalLoading] = useState(false);
  const [personalError, setPersonalError] = useState(null);
  const [showPersonalPassword, setShowPersonalPassword] = useState(false);

  // Форма нового канала
  const [newChannel, setNewChannel] = useState({
    channel_type: "telegram",
    bot_token: "",
    name: "",
  });

  // Форма AI ключа
  const [aiKey, setAiKey] = useState("");
  const [showAiKey, setShowAiKey] = useState(false);
  const pending = useRef(new Set());
  const personalAttempt = useRef(0);
  useEffect(() => () => { personalAttempt.current += 1; }, []);

  // Загрузка каналов
  const loadChannels = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const response = await api.get(`/bot/${companyId}/channels`);
      setChannels(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        handleError(error, "Ошибка загрузки каналов");
      }
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, handleError]);

  // Загрузка личных Telegram-каналов
  const loadPersonalChannels = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await api.get(`/telegram-personal/channels?company_id=${companyId}`);
      setPersonalChannels(res.data || []);
    } catch {
      setPersonalChannels([]);
    }
  }, [companyId]);

  useEffect(() => {
    loadChannels();
    loadPersonalChannels();
  }, [loadChannels, loadPersonalChannels]);

  // Загрузка настроек виджета
  useEffect(() => {
    if (!companyId) return;
    api.get(`/widget/config/${companyId}`)
      .then(res => {
        if (res.data.greeting) setWidgetGreeting(res.data.greeting);
        if (res.data.accent_color) setWidgetColor(res.data.accent_color);
      })
      .catch(() => {/* Используем дефолтные */});
  }, [companyId]);

  // Сохранение настроек виджета
  const handleSaveWidgetSettings = async () => {
    if (pending.current.has('widget')) return;
    if (!/^#[0-9a-f]{6}$/i.test(widgetColor)) {
      handleError(null, 'Введите цвет в формате #123456');
      return;
    }
    pending.current.add('widget');
    try {
      setWidgetSaving(true);
      await api.post(`/widget/settings/${companyId}`, {
        greeting: widgetGreeting,
        accent_color: widgetColor,
      });
      showSuccess("Настройки виджета сохранены");
    } catch (error) {
      handleError(error, "Ошибка сохранения настроек виджета");
    } finally {
      pending.current.delete('widget');
      setWidgetSaving(false);
    }
  };

  // Код для вставки на сайт
  const widgetEmbedCode = `<script src="https://prokatprost.ru/api/widget/js/rentuchet-widget.js" data-company-id="${companyId}" data-accent-color="${widgetColor}"></script>`;

  // Подключение Telegram бота
  const handleConnect = async () => {
    if (pending.current.has('bot')) return;
    if (!newChannel.bot_token.trim()) {
      handleError(null, "Введите токен бота");
      return;
    }

    pending.current.add('bot');
    try {
      setConnecting(true);
      await api.post(`/bot/${companyId}/connect-telegram`, {
        bot_token: newChannel.bot_token.trim(),
        name: newChannel.name.trim() || null,
      });
      showSuccess("Бот успешно подключён!");
      setDialogOpen(false);
      setNewChannel({ channel_type: "telegram", bot_token: "", name: "" });
      loadChannels();
    } catch (error) {
      handleError(error, "Ошибка подключения бота");
    } finally {
      pending.current.delete('bot');
      setConnecting(false);
    }
  };

  // Отключение канала
  const handleDisconnect = async (channelId) => {
    if (!window.confirm("Отключить этот канал? Вебхук будет удалён.")) return;

    try {
      await api.delete(`/bot/channel/${channelId}`);
      showSuccess("Канал отключён");
      loadChannels();
    } catch (error) {
      handleError(error, "Ошибка отключения канала");
    }
  };

  // Открытие диалога AI ключа
  const openAiDialog = async (channel) => {
    setSelectedChannel(channel);
    setAiKey("");
    setShowAiKey(false);

    // Загружаем статус канала
    try {
      const response = await api.get(`/bot/channel/${channel.id}/status`);
      setSelectedChannel({ ...channel, ...response.data });
    } catch (error) {
      // OK, просто используем данные из списка
    }

    setAiDialogOpen(true);
  };

  // Сохранение AI ключа
  const handleSaveAiKey = async () => {
    if (!selectedChannel || pending.current.has('ai')) return;
    pending.current.add('ai');

    try {
      const response = await api.post(
        `/bot/channel/${selectedChannel.id}/openai-key`,
        { openai_api_key: aiKey.trim() || null }
      );
      showSuccess(response.data.message);
      setAiDialogOpen(false);
      loadChannels();
    } catch (error) {
      handleError(error, "Ошибка сохранения ключа");
    } finally {
      pending.current.delete('ai');
    }
  };

  // Копирование в буфер
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess("Скопировано");
    } catch (error) {
      handleError(error, 'Не удалось скопировать код');
    }
  };

  const openPersonalDialog = () => {
    personalAttempt.current += 1;
    setPersonalStep('phone'); setPersonalPhone(''); setPersonalCode('');
    setPersonalPassword(''); setPersonalCodeHash(''); setPersonalNeed2FA(false);
    setShowPersonalPassword(false); setPersonalError(null); setPersonalLoading(false);
    setPersonalDialogOpen(true);
  };
  const closePersonalDialog = () => {
    personalAttempt.current += 1;
    setPersonalPassword(''); setPersonalCode(''); setPersonalCodeHash('');
    setPersonalDialogOpen(false);
  };

  // Личный Telegram: запросить код
  const handlePersonalRequestCode = async () => {
    if (personalLoading) return;
    if (!personalPhone || personalPhone.replace(/\D/g, '').length < 10) {
      setPersonalError('Введите номер телефона'); return;
    }
    const attempt = personalAttempt.current;
    try {
      setPersonalLoading(true); setPersonalError(null);
      const res = await api.post('/telegram-personal/request-code', {
        phone: personalPhone.replace(/\D/g, ''),
        company_id: parseInt(companyId),
      });
      if (attempt !== personalAttempt.current) return;
      setPersonalCodeHash(res.data.phone_code_hash);
      setPersonalStep('code');
    } catch (err) {
      if (attempt !== personalAttempt.current) return;
      setPersonalError(err.response?.data?.detail || 'Ошибка отправки кода');
    } finally { if (attempt === personalAttempt.current) setPersonalLoading(false); }
  };

  // Личный Telegram: подтвердить код
  const handlePersonalVerifyCode = async () => {
    if (personalLoading) return;
    if (!personalCode || personalCode.length < 4) {
      setPersonalError('Введите код'); return;
    }
    const attempt = personalAttempt.current;
    try {
      setPersonalLoading(true); setPersonalError(null);
      const res = await api.post('/telegram-personal/verify-code', {
        phone: personalPhone.replace(/\D/g, ''),
        code: personalCode,
        phone_code_hash: personalCodeHash,
        company_id: parseInt(companyId),
        password: personalPassword || undefined,
      });
      if (attempt !== personalAttempt.current) return;
      showSuccess(res.data.message);
      setPersonalDialogOpen(false);
      setPersonalStep('phone');
      setPersonalPhone(''); setPersonalCode(''); setPersonalPassword('');
      loadPersonalChannels();
    } catch (err) {
      if (attempt !== personalAttempt.current) return;
      const detail = err.response?.data?.detail || '';
      if (detail.toLowerCase().includes('2fa') || detail.toLowerCase().includes('пароль')) {
        setPersonalNeed2FA(true);
        setPersonalError('Требуется пароль двухфакторной аутентификации');
      } else {
        setPersonalError(detail || 'Ошибка подтверждения');
      }
    } finally { if (attempt === personalAttempt.current) setPersonalLoading(false); }
  };

  // Личный Telegram: отключить
  const handlePersonalDisconnect = async (channelId) => {
    if (!window.confirm('Отключить этот Telegram-аккаунт?')) return;
    try {
      await api.delete(`/telegram-personal/channel/${channelId}`);
      showSuccess('Канал отключён');
      loadPersonalChannels();
    } catch (err) {
      handleError(err, 'Ошибка отключения');
    }
  };

  if (!companyId) return <Alert severity="error">Компания не выбрана. Войдите в аккаунт компании.</Alert>;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Заголовок и кнопка */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6">Каналы связи</Typography>
          <Typography variant="body2" color="text.secondary">
            Подключите ботов для общения с клиентами через мессенджеры
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ textTransform: "none" }}
        >
          Подключить бота
        </Button>
      </Box>

      {/* Инструкция для пустого списка */}
      {channels.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <BotIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Нет подключённых каналов
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: "auto" }}>
            Подключите Telegram-бота, чтобы клиенты могли общаться с вашей
            компанией через мессенджер. Бот будет работать под вашим брендом.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Подключить первого бота
          </Button>
        </Paper>
      )}

      {/* Таблица каналов */}
      {channels.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Канал</TableCell>
                <TableCell>Бот</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>AI</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {channels.map((channel) => {
                const typeInfo = CHANNEL_TYPES[channel.channel_type] || {
                  label: channel.channel_type,
                  icon: <BotIcon />,
                  color: "#666",
                };

                return (
                  <TableRow key={channel.id} hover>
                    {/* Тип канала */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ color: typeInfo.color }}>{typeInfo.icon}</Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {channel.name || typeInfo.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {typeInfo.label}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Бот */}
                    <TableCell>
                      {channel.bot_username ? (
                        <Box>
                          <Typography variant="body2">
                            {channel.bot_display_name || channel.bot_username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{channel.bot_username}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* Статус */}
                    <TableCell>
                      {channel.webhook_active ? (
                        <Chip
                          icon={<LinkIcon />}
                          label="Активен"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<LinkOffIcon />}
                          label="Не активен"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </TableCell>

                    {/* AI */}
                    <TableCell>
                      <Tooltip
                        title={
                          channel.has_own_openai_key
                            ? "Свой ключ — безлимитный AI"
                            : `Платформа: ${channel.ai_usage?.used || 0} / ${channel.ai_usage?.limit || 500} сообщений`
                        }
                      >
                        <Chip
                          label={
                            channel.has_own_openai_key
                              ? "Свой ключ"
                              : `${channel.ai_usage?.used || 0} / ${channel.ai_usage?.limit || 500}`
                          }
                          size="small"
                          color={channel.has_own_openai_key ? "primary" : "default"}
                          variant="outlined"
                          onClick={() => openAiDialog(channel)}
                          sx={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    </TableCell>

                    {/* Действия */}
                    <TableCell align="right">
                      <Tooltip title="Настройки AI">
                        <IconButton
                          size="small"
                          onClick={() => openAiDialog(channel)}
                        >
                          <BotIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Отключить канал">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDisconnect(channel.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ═══ Диалог: Подключить бота ═══ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Подключить бота</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Тип канала */}
            <FormControl fullWidth>
              <InputLabel>Тип канала</InputLabel>
              <Select
                value={newChannel.channel_type}
                label="Тип канала"
                onChange={(e) =>
                  setNewChannel({ ...newChannel, channel_type: e.target.value })
                }
              >
                {Object.entries(CHANNEL_TYPES).map(([key, info]) => (
                  <MenuItem key={key} value={key} disabled={key !== "telegram"}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ color: info.color, display: "flex" }}>
                        {info.icon}
                      </Box>
                      {info.label}
                      {key !== "telegram" && (
                        <Chip label="Скоро" size="small" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Инструкция */}
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Как получить токен Telegram-бота:
              </Typography>
              <Typography variant="body2" component="div">
                1. Откройте <strong>@BotFather</strong> в Telegram
                <br />
                2. Отправьте команду <code>/newbot</code>
                <br />
                3. Введите имя бота (например, название вашей компании)
                <br />
                4. Скопируйте полученный токен и вставьте ниже
              </Typography>
            </Alert>

            {/* Название */}
            <TextField
              label="Название канала"
              placeholder="Например: Основной бот, Поддержка"
              value={newChannel.name}
              onChange={(e) =>
                setNewChannel({ ...newChannel, name: e.target.value })
              }
              fullWidth
              helperText="Необязательно — для вашего удобства"
            />

            {/* Токен */}
            <TextField
              label="Токен бота"
              placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
              value={newChannel.bot_token}
              onChange={(e) =>
                setNewChannel({ ...newChannel, bot_token: e.target.value })
              }
              fullWidth
              required
              type={showToken ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowToken(!showToken)}
                      edge="end"
                      size="small"
                    >
                      {showToken ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText="Токен от @BotFather. Мы проверим его и зарегистрируем webhook."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={connecting || !newChannel.bot_token.trim()}
            startIcon={connecting ? <CircularProgress size={16} /> : <LinkIcon />}
          >
            {connecting ? "Подключение..." : "Подключить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Диалог: Настройки AI ═══ */}
      <Dialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Настройки AI — {selectedChannel?.name || "Канал"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Текущий статус */}
            <Alert
              severity={selectedChannel?.has_own_openai_key ? "success" : "info"}
            >
              {selectedChannel?.has_own_openai_key ? (
                <Typography variant="body2">
                  Используется <strong>ваш ключ OpenAI</strong> — AI работает
                  без лимитов.
                </Typography>
              ) : (
                <Typography variant="body2">
                  Используется <strong>платформенный ключ</strong> — лимит{" "}
                  <strong>{selectedChannel?.ai_usage?.limit || 500}</strong>{" "}
                  AI-сообщений в месяц. Использовано:{" "}
                  <strong>{selectedChannel?.ai_usage?.used || 0}</strong>.
                </Typography>
              )}
            </Alert>

            <Divider />

            {/* Ввод ключа */}
            <Typography variant="body2" color="text.secondary">
              Вставьте свой OpenAI API ключ, чтобы снять лимит AI-сообщений.
              Оставьте пустым, чтобы использовать платформенный ключ с лимитом.
            </Typography>

            <TextField
              label="OpenAI API Key"
              placeholder="sk-..."
              value={aiKey}
              onChange={(e) => setAiKey(e.target.value)}
              fullWidth
              type={showAiKey ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowAiKey(!showAiKey)}
                      edge="end"
                      size="small"
                    >
                      {showAiKey ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText="Получите ключ на platform.openai.com/api-keys"
            />

            {selectedChannel?.has_own_openai_key && (
              <Alert severity="warning">
                Если сохранить пустое поле — вернётся платформенный ключ с
                лимитом {selectedChannel?.ai_usage?.limit || 500} сообщений/мес.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAiDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSaveAiKey}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Виджет для сайта ═══ */}
      <Box sx={{ mt: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6">
              <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#1976d2' }} />
              Виджет для сайта
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Вставьте одну строку кода на ваш сайт — AI-консультант начнёт работать автоматически
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ p: 3 }}>
          {/* Код для вставки */}
          <Typography variant="subtitle2" gutterBottom>
            Код для вставки на сайт
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Скопируйте этот код и вставьте перед закрывающим тегом <code>&lt;/body&gt;</code> на любой странице вашего сайта:
          </Typography>
          <Box sx={{
            bgcolor: '#1e1e1e',
            color: '#d4d4d4',
            p: 2,
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '13px',
            position: 'relative',
            overflowX: 'auto',
            mb: 3,
          }}>
            <Box component="code" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {widgetEmbedCode}
            </Box>
            <Tooltip title="Скопировать код">
              <IconButton
                size="small"
                sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                onClick={() => copyToClipboard(widgetEmbedCode)}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Настройки виджета */}
          <Typography variant="subtitle2" gutterBottom>
            Настройки виджета
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2, maxWidth: 500 }}>
            <TextField
              label="Приветственное сообщение"
              value={widgetGreeting}
              onChange={(e) => setWidgetGreeting(e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText="Первое сообщение, которое увидит посетитель при открытии чата"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label="Цвет виджета"
                value={widgetColor}
                onChange={(e) => setWidgetColor(e.target.value)}
                sx={{ width: 200 }}
                helperText="HEX-цвет кнопки и шапки"
              />
              <Box sx={{
                width: 48, height: 48, borderRadius: '50%', bgcolor: widgetColor,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)', flexShrink: 0,
                border: '2px solid #fff', outline: '1px solid #e0e0e0',
              }} />
              <input
                type="color"
                value={widgetColor}
                onChange={(e) => setWidgetColor(e.target.value)}
                style={{ width: 48, height: 48, border: 'none', cursor: 'pointer', background: 'none' }}
              />
            </Box>
            <Button
              variant="contained"
              onClick={handleSaveWidgetSettings}
              disabled={widgetSaving}
              startIcon={widgetSaving ? <CircularProgress size={16} /> : <CodeIcon />}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              {widgetSaving ? 'Сохранение...' : 'Сохранить настройки'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Инструкция */}
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Как работает виджет:
            </Typography>
            <Typography variant="body2" component="div">
              1. Скопируйте код выше и вставьте на свой сайт<br />
              2. На сайте появится кнопка-чат в правом нижнем углу<br />
              3. Посетитель нажимает → открывается чат с AI-консультантом<br />
              4. Бот отвечает на вопросы, помогает подобрать товар и оформить заявку<br />
              5. Заявки попадают в CRM → вы видите их в разделе «Заявки»
            </Typography>
          </Alert>
        </Paper>
      </Box>

      {/* ═══ Личный Telegram (userbot) ═══ */}
      <Box sx={{ mt: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6">Личный Telegram</Typography>
            <Typography variant="body2" color="text.secondary">
              Подключите рабочий номер — все входящие сообщения будут видны в мессенджере CRM
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<TelegramIcon />}
            onClick={openPersonalDialog}
            sx={{ textTransform: 'none', bgcolor: '#0088cc', '&:hover': { bgcolor: '#006699' } }}>
            Подключить аккаунт
          </Button>
        </Box>

        {personalChannels.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Нет подключённых аккаунтов
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 480, mx: 'auto' }}>
              Подключите личный номер сотрудника — все его переписки в Telegram будут
              отображаться в разделе «Мессенджер» наряду с Avito и другими каналами.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Аккаунт</TableCell>
                  <TableCell>Телефон</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {personalChannels.map((ch) => (
                  <TableRow key={ch.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TelegramIcon sx={{ color: '#0088cc' }} />
                        <Typography variant="body2" fontWeight={600}>
                          {ch.display_name || ch.name || 'Аккаунт'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{ch.phone || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {ch.connected ? (
                        <Chip icon={<CheckCircleIcon />} label="Активен" size="small" color="success" variant="outlined" />
                      ) : (
                        <Chip label="Не активен" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Отключить">
                        <IconButton size="small" color="error" onClick={() => handlePersonalDisconnect(ch.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ═══ Диалог: Подключить личный Telegram ═══ */}
      <Dialog open={personalDialogOpen} onClose={closePersonalDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Подключить личный Telegram</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {personalStep === 'phone' ? (
              <>
                <Alert severity="info">
                  <Typography variant="body2" fontWeight={600} gutterBottom>Как это работает:</Typography>
                  <Typography variant="body2">
                    1. Введите рабочий номер телефона сотрудника<br />
                    2. Telegram отправит код подтверждения<br />
                    3. После ввода кода — все сообщения видны в системе
                  </Typography>
                </Alert>
                <TextField fullWidth label="Номер телефона" placeholder="+79991234567"
                  value={personalPhone}
                  onChange={(e) => { setPersonalPhone(e.target.value); setPersonalError(null); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment> }}
                />
              </>
            ) : (
              <>
                <Alert severity="success">
                  Код отправлен в Telegram на номер <strong>{personalPhone}</strong>
                </Alert>
                <TextField fullWidth label="Код из Telegram" placeholder="12345"
                  value={personalCode}
                  onChange={(e) => { setPersonalCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setPersonalError(null); }}
                  inputProps={{ style: { textAlign: 'center', fontSize: 24, letterSpacing: 10, fontFamily: 'monospace' } }}
                />
                {personalNeed2FA && (
                  <TextField fullWidth label="Пароль двухфакторной аутентификации"
                    type={showPersonalPassword ? "text" : "password"}
                    value={personalPassword}
                    onChange={(e) => setPersonalPassword(e.target.value)}
                    helperText="Ваш облачный пароль Telegram (2FA)"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPersonalPassword(v => !v)} edge="end">
                            {showPersonalPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              </>
            )}
            {personalError && <Alert severity="error">{personalError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closePersonalDialog}>Отмена</Button>
          {personalStep === 'phone' ? (
            <Button variant="contained" onClick={handlePersonalRequestCode}
              disabled={personalLoading || !personalPhone}
              startIcon={personalLoading ? <CircularProgress size={16} /> : <PhoneIcon />}
              sx={{ textTransform: 'none', bgcolor: '#0088cc', '&:hover': { bgcolor: '#006699' } }}>
              {personalLoading ? 'Отправка...' : 'Получить код'}
            </Button>
          ) : (
            <Button variant="contained" onClick={handlePersonalVerifyCode}
              disabled={personalLoading || personalCode.length < 4}
              startIcon={personalLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              sx={{ textTransform: 'none' }}>
              {personalLoading ? 'Подключение...' : 'Подключить'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChannelSettings;
